/* ==========================================================================
   QUEBEC 2027 — JS propio del rediseño
   Archivo: assets/quebec-2027.js
   Se carga con defer desde las secciones que lo necesitan.
   ========================================================================== */


/* --------------------------------------------------------------------------
   <qb-carousel> — Carrusel de productos
   Usa el scroll nativo (scroll-snap). Las flechas avanzan una "página"
   (la cantidad de productos visibles) y se desactivan en los extremos.
   Marcado esperado:
     [data-qb-track]  → pista con overflow-x
     [data-qb-item]   → cada producto
     [data-qb-prev] / [data-qb-next] → botones
   -------------------------------------------------------------------------- */
if (!customElements.get('qb-carousel')) {
  class QbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-qb-track]');
      this.prevBtn = this.querySelector('[data-qb-prev]');
      this.nextBtn = this.querySelector('[data-qb-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);

      this.prevBtn?.addEventListener('click', this.onPrev);
      this.nextBtn?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.update, { passive: true });

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);

      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.update);
      this.prevBtn?.removeEventListener('click', this.onPrev);
      this.nextBtn?.removeEventListener('click', this.onNext);
    }

    /* Ancho de una "página": productos completos visibles × (ancho + gap) */
    pageWidth() {
      const item = this.track.querySelector('[data-qb-item]');
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item.getBoundingClientRect().width + gap;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      return perView * step;
    }

    go(direction) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: direction * this.pageWidth(),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      const tolerance = 2;

      if (this.prevBtn) this.prevBtn.disabled = x <= tolerance;
      if (this.nextBtn) this.nextBtn.disabled = x >= maxScroll - tolerance;

      /* Si todo cabe en pantalla, se ocultan las flechas (vía CSS) */
      this.toggleAttribute('data-static', maxScroll <= tolerance);
    }
  }

  customElements.define('qb-carousel', QbCarousel);
}


/* --------------------------------------------------------------------------
   Tarjeta de producto — selección de talla y validación
   - Al elegir una talla, se asigna su variante al formulario de la tarjeta.
   - Si se presiona "Agregar al carrito" sin talla, se muestra el aviso y
     se frena el envío ANTES de que actúe product-form.js de Dawn
     (escucha en fase de captura).
   -------------------------------------------------------------------------- */
if (!window.qbCardSizesReady) {
  window.qbCardSizesReady = true;

  document.addEventListener('change', (event) => {
    const input = event.target.closest('.qb-card__size-input');
    if (!input) return;

    const form = document.getElementById(input.dataset.form);
    if (!form) return;

    const idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.value = input.value;

    const hint = document.getElementById(form.dataset.hint);
    if (hint) hint.hidden = true;
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('qb-card__form')) return;

      const idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const hint = document.getElementById(form.dataset.hint);
      if (hint) hint.hidden = false;

      const firstSize = document.querySelector(`.qb-card__size-input[data-form="${form.id}"]:not(:disabled)`);
      if (firstSize) firstSize.focus();
    },
    true
  );
}


/* --------------------------------------------------------------------------
   <qb-card> — Tarjeta de producto
   Mide el alto real del bloque de tallas + botón (.qb-card__extra) y lo
   guarda en --qb-extra-h. El CSS usa ese valor para subir exactamente lo
   necesario al pasar el mouse (1 o 2 filas de tallas, sin scroll).
   -------------------------------------------------------------------------- */
if (!customElements.get('qb-card')) {
  class QbCard extends HTMLElement {
    connectedCallback() {
      this.extra = this.querySelector('.qb-card__extra');
      if (!this.extra) return;

      this.measure = () => {
        this.style.setProperty('--qb-extra-h', `${this.extra.offsetHeight}px`);

        /* Alto del botón "Ver tallas" (solo visible en táctil): el panel lo cubre */
        const toggle = this.querySelector('.qb-card__toggle');
        const toggleH =
          toggle && toggle.offsetParent !== null
            ? toggle.offsetHeight + (parseFloat(getComputedStyle(toggle).marginTop) || 0)
            : 0;
        this.style.setProperty('--qb-toggle-h', `${toggleH}px`);
      };

      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.extra);
      this.measure();

      /* Táctil: botón "Seleccionar talla" abre/cierra el panel */
      this.toggleBtn = this.querySelector('.qb-card__toggle');
      this.closeBtn = this.querySelector('.qb-card__close');
      if (this.toggleBtn) {
        this.onToggle = () => this.setOpen(true);
        this.onClose = () => {
          this.setOpen(false);
          this.toggleBtn.focus();
        };
        this.toggleBtn.addEventListener('click', this.onToggle);
        this.closeBtn?.addEventListener('click', this.onClose);
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.toggleBtn?.removeEventListener('click', this.onToggle);
      this.closeBtn?.removeEventListener('click', this.onClose);
    }

    setOpen(open) {
      /* Solo una tarjeta abierta a la vez */
      if (open) {
        document.querySelectorAll('qb-card[data-open]').forEach((card) => {
          if (card !== this && typeof card.setOpen === 'function') card.setOpen(false);
        });
      }

      if (open) this.measure();
      this.toggleAttribute('data-open', open);
      this.toggleBtn?.setAttribute('aria-expanded', String(open));
    }
  }

  customElements.define('qb-card', QbCard);

  /* Tocar fuera de una tarjeta abierta la cierra */
  document.addEventListener('click', (event) => {
    const openCard = document.querySelector('qb-card[data-open]');
    if (openCard && !openCard.contains(event.target)) openCard.setOpen(false);
  });
}


/* --------------------------------------------------------------------------
   <qb-slideshow> — Hero / banner principal
   - Flechas, puntos, swipe en móvil.
   - Avance automático (data-autoplay / data-speed en segundos). Se pausa al
     pasar el mouse, con el foco dentro, con la pestaña oculta y con
     "reducir movimiento". Botón de pausa/reproducir (accesibilidad).
   - data-under-header="true": si el hero es la primera sección y no hay
     secciones visibles entre el header y el hero, sube y queda detrás del
     header. Deja --qb-header-h en el hero y la clase qb-hero-under-header
     en <body> para que el header pueda volverse transparente.
   -------------------------------------------------------------------------- */
if (!customElements.get('qb-slideshow')) {
  class QbSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-qb-slides]');
      this.slides = [...this.querySelectorAll('[data-qb-slide]')];
      this.dots = [...this.querySelectorAll('[data-qb-dot]')];
      this.prevBtn = this.querySelector('[data-qb-prev]');
      this.nextBtn = this.querySelector('[data-qb-next]');
      this.toggleBtn = this.querySelector('[data-qb-toggle]');
      this.count = this.slides.length;
      this.index = 0;
      this.delay = (parseInt(this.dataset.speed, 10) || 6) * 1000;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && this.count > 1 && !reduceMotion;
      this.userPaused = !this.autoplay;
      this.holds = new Set(); /* pausas temporales: hover, foco, pestaña oculta */

      if (this.count > 1) this.bindControls();
      this.goTo(0, false);
      this.syncPausedState();
      this.setupUnderHeader();
    }

    disconnectedCallback() {
      this.stop();
      this.headerObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.body.classList.remove('qb-hero-under-header');
    }

    /* ---------- Controles ---------- */
    bindControls() {
      this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.toggleBtn?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncPausedState();
      });

      this.addEventListener('mouseenter', () => this.hold('hover'));
      this.addEventListener('mouseleave', () => this.release('hover'));
      /* Solo el foco de teclado pausa (un clic en flecha o pausa no la deja pegada) */
      this.addEventListener('focusin', (e) => {
        if (e.target.matches(':focus-visible')) this.hold('focus');
      });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.release('focus');
      });

      this.onVisibility = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));
      document.addEventListener('visibilitychange', this.onVisibility);

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
        if (e.key === 'ArrowRight') this.goTo(this.index + 1);
      });

      /* Swipe táctil (no afecta al mouse) */
      let startX = null;
      this.swiped = false;
      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        startX = e.clientX;
        this.swiped = false;
      });
      this.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 50) {
          this.swiped = true;
          this.goTo(this.index + (dx < 0 ? 1 : -1));
        }
      });
      /* Evita abrir el enlace del slide al terminar un swipe */
      this.addEventListener(
        'click',
        (e) => {
          if (this.swiped) {
            e.preventDefault();
            this.swiped = false;
          }
        },
        true
      );
    }

    goTo(i, fromUser = true) {
      this.index = (i + this.count) % this.count;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;

      this.slides.forEach((slide, k) => {
        const active = k === this.index;
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, k) => dot.setAttribute('aria-current', String(k === this.index)));

      if (fromUser) this.restart();
    }

    /* ---------- Avance automático ---------- */
    start() {
      if (!this.autoplay || this.userPaused || this.holds.size || this.timer) return;
      this.timer = setInterval(() => this.goTo(this.index + 1, false), this.delay);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    hold(reason) {
      this.holds.add(reason);
      this.stop();
    }

    release(reason) {
      this.holds.delete(reason);
      this.start();
    }

    syncPausedState() {
      this.toggleAttribute('data-paused', this.userPaused);
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute(
          'aria-label',
          this.userPaused ? 'Reanudar el avance automático' : 'Pausar el avance automático'
        );
      }
      this.userPaused ? this.stop() : this.start();
    }

    /* ---------- Detrás del header ---------- */
    setupUnderHeader() {
      if (this.dataset.underHeader !== 'true') return;

      const section = this.closest('.shopify-section');
      const header = document.querySelector('.section-header');
      const main = document.getElementById('MainContent');
      if (!section || !header || !main) return;

      const firstSection = main.querySelector(':scope > .shopify-section');
      if (firstSection !== section) {
        console.info('[Quebec hero] No se ubica detrás del header: el hero no es la primera sección de la página.');
        return;
      }

      const measure = () => {
        const groupSections = [...document.querySelectorAll('.shopify-section-group-header-group')];
        const between = groupSections
          .slice(groupSections.indexOf(header) + 1)
          .filter((el) => el.offsetHeight > 0);

        if (between.length) {
          section.style.marginTop = '';
          document.body.classList.remove('qb-hero-under-header');
          console.info(
            '[Quebec hero] No se ubica detrás del header: hay secciones visibles entre el header y el hero.',
            between
          );
          return;
        }

        const h = header.offsetHeight;
        section.style.marginTop = `-${h}px`;
        this.style.setProperty('--qb-header-h', `${h}px`);
        document.body.classList.add('qb-hero-under-header');
      };

      this.headerObserver = new ResizeObserver(measure);
      this.headerObserver.observe(header);
      measure();
    }
  }

  customElements.define('qb-slideshow', QbSlideshow);
}


/* --------------------------------------------------------------------------
   <qb-header> — Header
   - data-scrolled: se activa apenas hay scroll. El CSS lo usa para pasar de
     transparente (sobre el hero) a sólido carbón.
   - Escape dentro del mega menú: lo cierra y devuelve el foco al ítem.
   -------------------------------------------------------------------------- */
if (!customElements.get('qb-header')) {
  class QbHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => this.toggleAttribute('data-scrolled', window.scrollY > 10);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const item = e.target.closest('.qb-nav__item--mega');
        if (!item) return;
        const link = item.querySelector('.qb-nav__link');
        if (e.target !== link) {
          link.focus();
        } else {
          link.blur();
        }
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  customElements.define('qb-header', QbHeader);
}


/* --------------------------------------------------------------------------
   Secciones vacías del grupo del header (apps como Essential Countdown)
   Si una sección del grupo del header no tiene nada visible (ni texto, ni
   imagen, ni fondo con imagen), se colapsa a alto 0. Si después aparece
   contenido (la app lo inyecta o arranca una campaña), se vuelve a mostrar.
   Se colapsa con alto 0 (no display: none) para poder seguir midiendo el
   contenido y detectar cuándo vuelve a tener algo.
   Diagnóstico: agrega ?qb-debug=1 a la URL o ejecuta
   localStorage.setItem('qbDebug', '1') y recarga; revisa la consola.
   -------------------------------------------------------------------------- */
if (!window.__qbEmptyHeaderInit) {
  window.__qbEmptyHeaderInit = true;

  (() => {
    const DEBUG = /[?&]qb-debug=1/.test(location.search) || (() => {
      try { return localStorage.getItem('qbDebug') === '1'; } catch (e) { return false; }
    })();

    const MEDIA = 'img, picture, video, iframe, svg, canvas, object, embed';

    const isShown = (el) => {
      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };

    const inspect = (section) => {
      const text = (section.innerText || '').trim();
      const media = [...section.querySelectorAll(MEDIA)].filter(isShown);
      const backgrounds = [...section.querySelectorAll('*')].filter((el) => {
        const bg = getComputedStyle(el).backgroundImage;
        return bg && bg !== 'none' && isShown(el);
      });
      return {
        hasContent: text.length > 0 || media.length > 0 || backgrounds.length > 0,
        text: text.length,
        media: media.length,
        backgrounds: backgrounds.length,
      };
    };

    const candidates = () =>
      [...document.querySelectorAll('.shopify-section-group-header-group')].filter(
        (s) => !s.querySelector('qb-header') && !s.classList.contains('qb-header-section')
      );

    const evaluate = () => {
      const rows = [];
      candidates().forEach((section) => {
        const info = inspect(section);
        section.toggleAttribute('data-qb-empty', !info.hasContent);
        rows.push({
          id: section.id,
          colapsada: !info.hasContent,
          texto: info.text,
          medios: info.media,
          fondos: info.backgrounds,
          altoContenido: Math.round(section.scrollHeight),
        });
      });

      if (DEBUG) {
        console.groupCollapsed('[qb] Secciones del header evaluadas');
        console.table(rows);
        /* Todo lo que está antes del <main>, para ubicar espacios que no vienen de una sección */
        const before = [];
        for (const el of document.body.children) {
          if (el.id === 'MainContent' || el.tagName === 'MAIN') break;
          const cs = getComputedStyle(el);
          before.push({
            el: `${el.tagName.toLowerCase()}${el.id ? '#' + el.id : ''}${el.className && typeof el.className === 'string' ? '.' + el.className.trim().split(/\s+/).join('.') : ''}`,
            alto: Math.round(el.getBoundingClientRect().height),
            margenArriba: cs.marginTop,
            margenAbajo: cs.marginBottom,
            display: cs.display,
            position: cs.position,
          });
        }
        console.table(before);
        console.groupEnd();
      }
    };

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        evaluate();
      });
    };

    const observe = () => {
      const observer = new MutationObserver(schedule);
      candidates().forEach((section) => {
        observer.observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        /* Imágenes que terminan de cargar dentro de la sección */
        section.addEventListener('load', schedule, true);
      });
    };

    const init = () => {
      evaluate();
      observe();
      window.addEventListener('load', schedule);
      window.addEventListener('resize', schedule);
      /* Algunas apps inyectan su contenido tarde */
      setTimeout(schedule, 1500);
      setTimeout(schedule, 4000);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  })();
}